import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { AdsConfirmDialog } from "@/components/ads-assistant/AdsConfirmDialog";
import { AchievementBadge } from "@/components/common/kpi/AchievementBadge";
import { useUserAchievements } from "@/hooks/useUserAchievements";
import { canEditAdsGuidance } from "@/lib/adsAssistantPermissions";

function formatUnlockedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export function AchievementGalleryDrawer(props: {
  open: boolean;
  onClose: () => void;
  viewUserId?: string | null;
}) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const auth = useAuth();
  const scopedOrgId = useScopedOrganizationId();
  const canRevoke = canEditAdsGuidance(auth);
  const [pendingRevokeId, setPendingRevokeId] = useState<string | null>(null);

  const vm = useUserAchievements({
    enabled: props.open,
    scopedOrgId,
    viewUserId: props.viewUserId,
  });

  useEffect(() => {
    if (!props.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [props.open, props.onClose]);

  useEffect(() => {
    if (!props.open) return;
    const onMouse = (e: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target as Node)) props.onClose();
    };
    const id = window.setTimeout(() => document.addEventListener("mousedown", onMouse), 50);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("mousedown", onMouse);
    };
  }, [props.open, props.onClose]);

  const title = vm.displayName ? `${vm.displayName}'s achievements` : "Your achievements";

  return (
    <>
      {props.open ? (
        <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]" aria-hidden />
      ) : null}

      <div
        ref={drawerRef}
        className={
          "fixed top-0 right-0 h-full w-96 max-w-full z-50 shadow-2xl flex flex-col " +
          "bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 " +
          "transform transition-transform duration-300 ease-in-out " +
          (props.open ? "translate-x-0" : "translate-x-full pointer-events-none")
        }
        role={props.open ? "dialog" : undefined}
        aria-modal={props.open ? true : undefined}
        aria-labelledby={props.open ? "achievement-gallery-title" : undefined}
        inert={!props.open}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 id="achievement-gallery-title" className="text-base font-semibold pr-2">
            {title}
          </h2>
          <button
            type="button"
            onClick={props.onClose}
            aria-label="Close achievements"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Badges you have earned in this organization.
            </p>
            <button
              type="button"
              onClick={() => void vm.refresh()}
              disabled={vm.loading}
              className="shrink-0 text-xs font-medium text-blue-700 hover:underline disabled:opacity-50 dark:text-blue-300"
            >
              Refresh
            </button>
          </div>

          {vm.error ? (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950 dark:border-amber-900 dark:bg-amber-950/30">
              {vm.error}
            </p>
          ) : null}

          {vm.loading && !vm.achievements.length ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : null}

          {!vm.loading && !vm.error && vm.achievements.length === 0 ? (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              No achievements yet — log ads and complete training to earn badges.
            </p>
          ) : null}

          <ul className="space-y-3">
            {vm.achievements.map((a) => {
              const periodKey = (a.metadata as Record<string, unknown> | undefined)?.period_key;
              return (
                <li
                  key={a.id}
                  className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2 dark:border-gray-600 dark:bg-zinc-800/50"
                >
                  <div className="min-w-0 space-y-1">
                    <AchievementBadge achievementKey={a.achievement_key} metadata={a.metadata} />
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      {formatUnlockedAt(a.unlocked_at)}
                      {typeof periodKey === "string" && periodKey ? ` · ${periodKey}` : ""}
                    </p>
                  </div>
                  {canRevoke ? (
                    <button
                      type="button"
                      onClick={() => setPendingRevokeId(a.id)}
                      disabled={vm.revokingId === a.id}
                      className="shrink-0 text-[11px] font-medium text-red-700 hover:underline disabled:opacity-50 dark:text-red-300"
                    >
                      Revoke
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <AdsConfirmDialog
        open={pendingRevokeId !== null}
        title="Revoke achievement?"
        message="This removes the badge from the user's gallery. They may earn it again if the activity qualifies."
        confirmLabel="Revoke"
        cancelLabel="Cancel"
        danger
        onCancel={() => setPendingRevokeId(null)}
        onConfirm={() => {
          const id = pendingRevokeId;
          setPendingRevokeId(null);
          if (id) void vm.revoke(id);
        }}
      />
    </>
  );
}
